import { useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"

import { DashboardLayout } from "../../layouts/DashboardLayout"
import { deleteModuleApi, getModuleApi, ModuleDetail } from "../../features/modules/api/modules.api"
import { deleteLessonApi, listLessonsApi, LessonListItem } from "../../features/lessons/api/lesson.api"
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  BookOpenIcon,
  ChevronLeftIcon,
  LockClosedIcon,
  GiftIcon,
  FilmIcon,
  DocumentTextIcon,
} from "@heroicons/react/16/solid"

type Tab = "overview" | "lessons" | "settings"

const TAB_LABELS: Record<Tab, string> = {
  overview: "Обзор",
  lessons: "Уроки",
  settings: "Настройки",
}

/* ── helpers ─────────────────────────────────────────── */

const TypeIcon = ({ type }: { type: string }) => {
  if (type === "video") return <FilmIcon className="w-3.5 h-3.5 text-blue-500" />
  return <DocumentTextIcon className="w-3.5 h-3.5 text-gray-400" />
}

/* ── page ─────────────────────────────────────────────── */

export const ModuleDetailPage = () => {
  const { courseId, moduleId } = useParams<{ courseId: string; moduleId: string }>()
  const navigate = useNavigate()
  const isAdmin = localStorage.getItem("role") === "admin"

  const [module, setModule] = useState<ModuleDetail | null>(null)
  const [lessons, setLessons] = useState<LessonListItem[]>([])
  const [tab, setTab] = useState<Tab>("lessons")
  const [loading, setLoading] = useState(true)

  const TABS: Tab[] = isAdmin ? ["lessons", "overview", "settings"] : ["lessons", "overview"]

  useEffect(() => {
    if (!courseId || !moduleId) return
    setLoading(true)
    Promise.all([
      getModuleApi(courseId, moduleId),
      listLessonsApi(moduleId),
    ])
      .then(([moduleRes, lessonsRes]) => {
        setModule(moduleRes.data)
        setLessons(lessonsRes.data.items)
      })
      .finally(() => setLoading(false))
  }, [courseId, moduleId])

  const sortedLessons = useMemo(
    () => [...lessons].sort((a, b) => a.order_number - b.order_number),
    [lessons]
  )

  if (loading) {
    return (
      <DashboardLayout title="Модуль">
        <div className="space-y-4 animate-pulse">
          <div className="h-28 rounded-xl bg-gray-100" />
          <div className="h-8 w-64 rounded-lg bg-gray-100" />
          <div className="space-y-3">
            {[1,2,3].map(i => <div key={i} className="h-16 rounded-xl bg-gray-100" />)}
          </div>
        </div>
      </DashboardLayout>
    )
  }

  if (!module) {
    return (
      <DashboardLayout title="Модуль">
        <div className="py-20 text-center text-gray-400">Модуль не найден</div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout title={module.title}>
      <div className="space-y-5">

        {/* ── HEADER CARD ── */}
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <button
                onClick={() => navigate(`/courses/${courseId}`)}
                className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-blue-500 transition-colors mb-3"
              >
                <ChevronLeftIcon className="w-3.5 h-3.5" />
                Назад к курсу
              </button>
              <h1 className="text-xl font-bold text-gray-900">{module.title}</h1>
              {module.description && (
                <p className="text-sm text-gray-500 mt-1">{module.description}</p>
              )}
              <div className="flex gap-4 mt-3 text-xs text-gray-400">
                <span className="flex items-center gap-1">
                  <BookOpenIcon className="w-3.5 h-3.5" />
                  {module.lessons_count ?? sortedLessons.length} уроков
                </span>
                {module.duration_minutes != null && (
                  <span className="flex items-center gap-1">
                    <ClockIcon className="w-3.5 h-3.5" />
                    {module.duration_minutes} мин
                  </span>
                )}
                <span className="font-mono text-gray-300"># {module.order_number}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── TABS ── */}
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium capitalize transition-all ${
                tab === t ? "bg-white text-gray-800 shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {t === "lessons" ? `Уроки (${sortedLessons.length})` : TAB_LABELS[t]}
            </button>
          ))}
        </div>

        {/* ── OVERVIEW ── */}
        {tab === "overview" && (
          <div className="bg-white rounded-xl border p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Описание</h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              {module.description || <span className="text-gray-400 italic">Нет описания</span>}
            </p>
          </div>
        )}

        {/* ── LESSONS ── */}
        {tab === "lessons" && (
          <LessonsTab
            lessons={sortedLessons}
            moduleId={moduleId!}
            canManageStructure={isAdmin}
            onView={(lessonId) => navigate(`/modules/${moduleId}/lessons/${lessonId}`)}
            onAdd={() => navigate(`/modules/${moduleId}/lessons/create`)}
            onDelete={async (lessonId) => {
              if (!window.confirm("Удалить этот урок?")) return
              await deleteLessonApi(lessonId)
              setLessons((prev) => prev.filter((l) => l.id !== lessonId))
            }}
          />
        )}

        {/* ── SETTINGS ── */}
        {tab === "settings" && isAdmin && (
          <ModuleSettings
            onDelete={async () => {
              if (!window.confirm("Вы уверены, что хотите удалить этот модуль?\nВсе уроки будут удалены.")) return
              await deleteModuleApi(module.id)
              navigate(`/courses/${courseId}`)
            }}
          />
        )}
      </div>
    </DashboardLayout>
  )
}

/* ── lessons tab ──────────────────────────────────────── */

const LessonsTab = ({
  lessons,
  moduleId,
  canManageStructure,
  onView,
  onAdd,
  onDelete,
}: {
  lessons: LessonListItem[]
  moduleId: string
  canManageStructure: boolean
  onView: (id: string) => void
  onAdd: () => void
  onDelete: (id: string) => void
}) => (
  <div className="space-y-2.5">
    {lessons.length === 0 && (
      <div className="bg-white rounded-xl border py-14 text-center">
        <BookOpenIcon className="w-8 h-8 text-gray-200 mx-auto mb-2" />
        <p className="text-sm text-gray-400">Уроков пока нет</p>
      </div>
    )}

    {lessons.map((l) => (
      <div
        key={l.id}
        onClick={() => onView(l.id)}
        className="group bg-white border rounded-xl px-5 py-4 flex items-center justify-between hover:border-blue-200 hover:shadow-sm transition-all cursor-pointer"
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-7 h-7 rounded-lg bg-gray-50 border text-xs font-semibold text-gray-400 flex items-center justify-center flex-shrink-0">
            {l.order_number + 1}
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <TypeIcon type={l.type} />
              <span className="font-medium text-gray-800 text-sm truncate">{l.title}</span>
            </div>
            <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
              {l.duration && (
                <span className="flex items-center gap-1">
                  <ClockIcon className="w-3 h-3" />{l.duration}
                </span>
              )}
              {l.access_type === "free" ? (
                <span className="flex items-center gap-1 text-emerald-600">
                  <GiftIcon className="w-3 h-3" />Бесплатно
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <LockClosedIcon className="w-3 h-3" />Платно
                </span>
              )}
              <span className="capitalize">{l.type}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition flex-shrink-0 ml-4">
          <button
            onClick={(e) => { e.stopPropagation(); onView(l.id) }}
            className="inline-flex items-center gap-1 text-xs font-medium text-gray-400 hover:text-blue-600 px-2 py-1.5 rounded-lg hover:bg-blue-50 transition-colors"
          >
            <PencilSquareIcon className="w-3.5 h-3.5" />
          </button>
          {canManageStructure && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(l.id) }}
              className="inline-flex items-center gap-1 text-xs font-medium text-gray-400 hover:text-red-500 px-2 py-1.5 rounded-lg hover:bg-red-50 transition-colors"
            >
              <TrashIcon className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    ))}

    {canManageStructure && (
      <button
        onClick={onAdd}
        className="w-full border-2 border-dashed border-gray-200 rounded-xl py-4 text-sm font-medium text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors flex items-center justify-center gap-2"
      >
        <PlusIcon className="w-4 h-4" />
        Добавить урок
      </button>
    )}
  </div>
)

/* ── settings ─────────────────────────────────────────── */

const ModuleSettings = ({ onDelete }: { onDelete: () => void }) => (
  <div className="max-w-lg">
    <div className="rounded-xl border border-red-200 bg-red-50 p-5">
      <div className="flex items-start gap-3">
        <ExclamationTriangleIcon className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
        <div>
          <h3 className="font-semibold text-red-700 text-sm">Опасная зона</h3>
          <p className="text-sm text-red-600 mt-1 leading-relaxed">
            Удаление этого модуля приведёт к безвозвратному удалению всех его уроков.
          </p>
          <button
            onClick={onDelete}
            className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors shadow-sm"
          >
            <TrashIcon className="w-4 h-4" />
            Удалить модуль
          </button>
        </div>
      </div>
    </div>
  </div>
)
